"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parsePriceData = exports.parseProductData = exports.parseMappingData = exports.parseBaseData = exports.AccountType = exports.DeriveType = exports.PriceType = exports.CorpAction = exports.PriceStatus = exports.MAX_SLOT_DIFFERENCE = exports.Version = exports.Version2 = exports.Magic = void 0;
const web3_js_1 = require("@solana/web3.js");
const buffer_1 = require("buffer");
const numbers_1 = require("../numbers");
const readBig_1 = require("./readBig");
/** Constants. This section must be kept in sync with the on-chain program. */
exports.Magic = 0xa1b2c3d4;
exports.Version2 = 2;
exports.Version = exports.Version2;
/** Number of slots that can pass before a publisher's price is no longer included in the aggregate. */
exports.MAX_SLOT_DIFFERENCE = 25;
var PriceStatus;
(function (PriceStatus) {
    PriceStatus[PriceStatus["Unknown"] = 0] = "Unknown";
    PriceStatus[PriceStatus["Trading"] = 1] = "Trading";
    PriceStatus[PriceStatus["Halted"] = 2] = "Halted";
    PriceStatus[PriceStatus["Auction"] = 3] = "Auction";
})(PriceStatus = exports.PriceStatus || (exports.PriceStatus = {}));
var CorpAction;
(function (CorpAction) {
    CorpAction[CorpAction["NoCorpAct"] = 0] = "NoCorpAct";
})(CorpAction = exports.CorpAction || (exports.CorpAction = {}));
var PriceType;
(function (PriceType) {
    PriceType[PriceType["Unknown"] = 0] = "Unknown";
    PriceType[PriceType["Price"] = 1] = "Price";
})(PriceType = exports.PriceType || (exports.PriceType = {}));
var DeriveType;
(function (DeriveType) {
    DeriveType[DeriveType["Unknown"] = 0] = "Unknown";
    DeriveType[DeriveType["TWAP"] = 1] = "TWAP";
    DeriveType[DeriveType["Volatility"] = 2] = "Volatility";
})(DeriveType = exports.DeriveType || (exports.DeriveType = {}));
var AccountType;
(function (AccountType) {
    AccountType[AccountType["Unknown"] = 0] = "Unknown";
    AccountType[AccountType["Mapping"] = 1] = "Mapping";
    AccountType[AccountType["Product"] = 2] = "Product";
    AccountType[AccountType["Price"] = 3] = "Price";
    AccountType[AccountType["Test"] = 4] = "Test";
})(AccountType = exports.AccountType || (exports.AccountType = {}));
const empty32Buffer = buffer_1.Buffer.alloc(32);
const PKorNull = (data) => (data.equals(empty32Buffer) ? null : new web3_js_1.PublicKey(data));
/** Parse data as a generic Pyth account. Use this method if you don't know the account type. */
function parseBaseData(data) {
    // data is too short to have the magic number.
    if (data.byteLength < 4) {
        return undefined;
    }
    const magic = data.readUInt32LE(0);
    if (magic === exports.Magic) {
        // program version
        const version = data.readUInt32LE(4);
        // account type
        const type = data.readUInt32LE(8);
        // account used size
        const size = data.readUInt32LE(12);
        return { magic, version, type, size };
    }
    else {
        return undefined;
    }
}
exports.parseBaseData = parseBaseData;
const parseMappingData = (data) => {
    // pyth magic number
    const magic = data.readUInt32LE(0);
    // program version
    const version = data.readUInt32LE(4);
    // account type
    const type = data.readUInt32LE(8);
    // account used size
    const size = data.readUInt32LE(12);
    // number of product accounts
    const numProducts = data.readUInt32LE(16);
    // unused
    // const unused = accountInfo.data.readUInt32LE(20)
    // next mapping account (if any)
    const nextMappingAccount = PKorNull(data.slice(24, 56));
    // read each symbol account
    let offset = 56;
    const productAccountKeys = [];
    for (let i = 0; i < numProducts; i++) {
        const productAccountBytes = data.slice(offset, offset + 32);
        const productAccountKey = new web3_js_1.PublicKey(productAccountBytes);
        offset += 32;
        productAccountKeys.push(productAccountKey);
    }
    return {
        magic,
        version,
        type,
        size,
        nextMappingAccount,
        productAccountKeys,
    };
};
exports.parseMappingData = parseMappingData;
const parseProductData = (data) => {
    // pyth magic number
    const magic = data.readUInt32LE(0);
    // program version
    const version = data.readUInt32LE(4);
    // account type
    const type = data.readUInt32LE(8);
    // price account size
    const size = data.readUInt32LE(12);
    // first price account in list
    const priceAccountBytes = data.slice(16, 48);
    const priceAccountKey = new web3_js_1.PublicKey(priceAccountBytes);
    const product = {};
    let idx = 48;
    while (idx < size) {
        const keyLength = data[idx];
        idx++;
        if (keyLength) {
            const key = data.slice(idx, idx + keyLength).toString();
            idx += keyLength;
            const valueLength = data[idx];
            idx++;
            const value = data.slice(idx, idx + valueLength).toString();
            idx += valueLength;
            product[key] = value;
        }
    }
    return { magic, version, type, size, priceAccountKey, product };
};
exports.parseProductData = parseProductData;
const parseEma = (data, exponent) => {
    // current value of ema
    const valueComponent = (0, readBig_1.readBigInt64LE)(data, 0);
    const value = Number(valueComponent) * Math.pow(10, exponent);
    // numerator state for next update
    const numerator = (0, readBig_1.readBigInt64LE)(data, 8);
    // denominator state for next update
    const denominator = (0, readBig_1.readBigInt64LE)(data, 16);
    return { valueComponent, value, numerator, denominator };
};
const parsePriceInfo = (data, exponent) => {
    // aggregate price
    const priceComponent = (0, readBig_1.readBigInt64LE)(data, 0);
    const price = (0, numbers_1.expo)(priceComponent.toString(), exponent).toNumber();
    // aggregate confidence
    const confidenceComponent = (0, readBig_1.readBigUInt64LE)(data, 8);
    const confidence = Number(confidenceComponent) * Math.pow(10, exponent);
    // aggregate status
    const status = data.readUInt32LE(16);
    // aggregate corporate action
    const corporateAction = data.readUInt32LE(20);
    // aggregate publish slot. It is converted to number to be consistent with Solana's library interface (Slot there is number)
    const publishSlot = Number((0, readBig_1.readBigUInt64LE)(data, 24));
    return {
        priceComponent,
        price,
        confidenceComponent,
        confidence,
        status,
        corporateAction,
        publishSlot,
    };
};
// Provide currentSlot when available to allow status to consider the case when price goes stale. It is optional because
// it requires an extra request to get it when it is not available which is not always efficient.
const parsePriceData = (data, currentSlot) => {
    // pyth magic number
    const magic = data.readUInt32LE(0);
    // program version
    const version = data.readUInt32LE(4);
    // account type
    const type = data.readUInt32LE(8);
    // price account size
    const size = data.readUInt32LE(12);
    // price or calculation type
    const priceType = data.readUInt32LE(16);
    // price exponent
    const exponent = data.readInt32LE(20);
    // number of component prices
    const numComponentPrices = data.readUInt32LE(24);
    // number of quoters that make up aggregate
    const numQuoters = data.readUInt32LE(28);
    // slot of last valid (not unknown) aggregate price
    const lastSlot = (0, readBig_1.readBigUInt64LE)(data, 32);
    // valid on-chain slot of aggregate price
    const validSlot = (0, readBig_1.readBigUInt64LE)(data, 40);
    // time-weighted average price
    const twap = parseEma(data.slice(48, 72), exponent);
    // time-weighted average confidence interval
    const twac = parseEma(data.slice(72, 96), exponent);
    // space for future derived values
    const drv1Component = (0, readBig_1.readBigInt64LE)(data, 96);
    const drv1 = Number(drv1Component) * Math.pow(10, exponent);
    // minimum number of publishers for status to be TRADING
    const minPublishers = data.readUInt8(104);
    // space for future derived values
    const drv2 = data.readInt8(105);
    // space for future derived values
    const drv3 = data.readInt16LE(106);
    // space for future derived values
    const drv4 = data.readInt32LE(108);
    // product id / reference account
    const productAccountKey = new web3_js_1.PublicKey(data.slice(112, 144));
    // next price account in list
    const nextPriceAccountKey = PKorNull(data.slice(144, 176));
    // valid slot of previous update
    const previousSlot = (0, readBig_1.readBigUInt64LE)(data, 176);
    // aggregate price of previous update
    const previousPriceComponent = (0, readBig_1.readBigInt64LE)(data, 184);
    const previousPrice = Number(previousPriceComponent) * Math.pow(10, exponent);
    // confidence interval of previous update
    const previousConfidenceComponent = (0, readBig_1.readBigUInt64LE)(data, 192);
    const previousConfidence = Number(previousConfidenceComponent) * Math.pow(10, exponent);
    // space for future derived values
    const drv5Component = (0, readBig_1.readBigInt64LE)(data, 200);
    const drv5 = Number(drv5Component) * Math.pow(10, exponent);
    const aggregate = parsePriceInfo(data.slice(208, 240), exponent);
    let status = aggregate.status;
    if (currentSlot && status === PriceStatus.Trading) {
        if (currentSlot - aggregate.publishSlot > exports.MAX_SLOT_DIFFERENCE) {
            status = PriceStatus.Unknown;
        }
    }
    let price;
    let confidence;
    if (status === PriceStatus.Trading) {
        price = aggregate.price;
        confidence = aggregate.confidence;
    }
    // price components - up to 32
    const priceComponents = [];
    let offset = 240;
    let shouldContinue = true;
    while (offset < data.length && shouldContinue) {
        const publisher = PKorNull(data.slice(offset, offset + 32));
        offset += 32;
        if (publisher) {
            const componentAggregate = parsePriceInfo(data.slice(offset, offset + 32), exponent);
            offset += 32;
            const latest = parsePriceInfo(data.slice(offset, offset + 32), exponent);
            offset += 32;
            priceComponents.push({ publisher, aggregate: componentAggregate, latest });
        }
        else {
            shouldContinue = false;
        }
    }
    return {
        magic,
        version,
        type,
        size,
        priceType,
        exponent,
        numComponentPrices,
        numQuoters,
        lastSlot,
        validSlot,
        twap,
        twac,
        drv1Component,
        drv1,
        minPublishers,
        drv2,
        drv3,
        drv4,
        productAccountKey,
        nextPriceAccountKey,
        previousSlot,
        previousPriceComponent,
        previousPrice,
        previousConfidenceComponent,
        previousConfidence,
        drv5Component,
        drv5,
        aggregate,
        priceComponents,
        price,
        confidence,
        status,
    };
};
exports.parsePriceData = parsePriceData;
